import React, {ReactElement, useEffect, useState} from 'react';
import useMounted from '../../../../../hooks/use-mounted/use-mounted';
import Icon from '../../../../atoms/Icon/Icon';
import SizeAtom from '../../../../atoms/Size/Size';
import {ShortSizeType} from '../../../../atoms/Size/typings';
import Accordion from '../../../Accordion/Accordion';
import Textfield from '../../../Textfield/Textfield';
import StyledSize, {SectionHeader, StyledSizeBody, TextFieldGroup} from './styles';
import Props, {ErrorProps, SizeProps, SizeType} from './typings';

const initialInputState = {
  height: {
    min: {
      value: '',
      error: false,
    },
    max: {
      value: '',
      error: false,
    },
  },
  width: {
    min: {
      value: '',
      error: false,
    },
    max: {
      value: '',
      error: false,
    },
  },
};

const sizeNames = {
  S: 'small',
  M: 'medium',
  L: 'large',
  XL: 'xlarge',
} as const;

const sizeLabels = {
  cm: {
    S: '<50',
    M: '51-100',
    L: '101-150',
    XL: '>150',
  },
  in: {
    S: '<19.7',
    M: '19.7-39.4',
    L: '39.4-59.1',
    XL: '>59.1',
  },
} as const;

export const getSizeTitle = (size, widthMax, widthMin, heightMax, heightMin, displaySizeUnits) => {
  const titleArray: string[] = [];
  let selectedFilters = 0;
  if (size) {
    titleArray.push(
      `W${
        sizeLabels[displaySizeUnits][Object.keys(sizeNames).find((key) => sizeNames[key] === size)]
      }${displaySizeUnits}`,
    );
    selectedFilters++;
  } else {
    if (widthMin && !widthMax) {
      titleArray.push(`W>${widthMin}${displaySizeUnits}`);
      selectedFilters++;
    } else if (!widthMin && widthMax) {
      titleArray.push(`W<${widthMax}${displaySizeUnits}`);
      selectedFilters++;
    } else if (widthMin && widthMax) {
      titleArray.push(`W${widthMin}-${widthMax}${displaySizeUnits}`);
      selectedFilters++;
    }

    if (heightMin && !heightMax) {
      titleArray.push(`H>${heightMin}${displaySizeUnits}`);
      selectedFilters++;
    } else if (!heightMin && heightMax) {
      titleArray.push(`H<${heightMax}${displaySizeUnits}`);
      selectedFilters++;
    } else if (heightMin && heightMax) {
      titleArray.push(`H${heightMin}-${heightMax}${displaySizeUnits}`);
      selectedFilters++;
    }
  }
  return {label: titleArray, count: selectedFilters};
};

const Size = ({
  size,
  widthMin,
  widthMax,
  heightMin,
  heightMax,
  displaySizeUnits,
  collapsible = false,
  defaultCollapsed = false,
  sizeCallback,
  errorCallback,
}: Props): ReactElement => {
  const title = 'Size';
  const subtitle = 'Find an artwork with a size that meets your needs.';
  const hasMounted = useMounted();

  const [inputFields, setInputFields] = useState({
    height: {
      min: {
        value: heightMin ? heightMin : '',
        error: false,
      },
      max: {
        value: heightMax ? heightMax : '',
        error: false,
      },
    },
    width: {
      min: {
        value: widthMin ? widthMin : '',
        error: false,
      },
      max: {
        value: widthMax ? widthMax : '',
        error: false,
      },
    },
  });
  const [errorList, setErrorList] = useState<ErrorProps[]>([]);
  const [response, setResponse] = useState<SizeProps>({
    size_: null,
    width_min: null,
    width_max: null,
    height_min: null,
    height_max: null,
  });

  useEffect(() => {
    errorCallback(errorList.length > 0);
  }, [errorList]);

  useEffect(() => {
    if (!hasMounted) return;
    sizeCallback(response);
  }, [response]);

  const isNumber = (newValue) => {
    if (isNaN(+newValue)) {
      return false;
    }
    return true;
  };

  const onSizeBtnClick = (newSize: SizeType) => {
    setResponse({
      size_: newSize === size ? null : newSize,
      width_min: null,
      width_max: null,
      height_min: null,
      height_max: null,
    });
    setInputFields(initialInputState);
  };

  const onInputChange = (group, input, value) => {
    if (!isNumber(value)) return;

    const updatedState = {
      ...inputFields,
      [group]: {
        ...inputFields[group],
        [input]: {
          value: value,
          error: inputFields[group][input].error,
        },
      },
    };
    errorCheck(group, updatedState);
  };

  const convertToNumber = (value) => (Number(value) === 0 ? null : Number(value));

  const errorCheck = (group, state) => {
    const errors: ErrorProps[] = [];
    const inputCouple = ['min', 'max'];
    let minHasError = false;
    let maxHasError = false;

    if (state[group].min.value !== '' && state[group].max.value !== '') {
      if (parseInt(state[group].min.value) > parseInt(state[group].max.value)) {
        errors.push({group: group, msg: `Maximum ${group} value must be bigger than minimum`});
        minHasError = true;
        maxHasError = true;
      }
    }

    inputCouple.map((input) => {
      if (state[group][input].value % 1 !== 0) {
        errors.push({group: group, msg: 'Decimals are not supported'});
        if (input === 'min') {
          minHasError = true;
        } else {
          maxHasError = true;
        }
      }
    });

    const newState = {
      ...state,
      [group]: {
        min: {
          value: state[group].min.value,
          error: minHasError,
        },
        max: {
          value: state[group].max.value,
          error: maxHasError,
        },
      },
    };

    setInputFields(newState);

    if (!maxHasError && !minHasError) {
      setResponse({
        size_: null,
        width_min: convertToNumber(newState.width.min.value),
        width_max: convertToNumber(newState.width.max.value),
        height_min: convertToNumber(newState.height.min.value),
        height_max: convertToNumber(newState.height.max.value),
      });
    }

    const newErrorList = errorList.filter((error) => error.group !== group);
    newErrorList.push(...errors);
    setErrorList(newErrorList);
  };

  const capitalize = (string): string => string[0].toUpperCase() + string.slice(1);

  const fragmentBody = () => (
    <StyledSizeBody>
      <SectionHeader>Select a size ({displaySizeUnits})</SectionHeader>

      <section>
        {Object.keys(sizeNames).map((sizeLabel: ShortSizeType, key) => (
          <SizeAtom
            size={sizeLabel}
            active={sizeNames[sizeLabel] === size}
            label={sizeLabels[displaySizeUnits][sizeLabel]}
            onClick={() => onSizeBtnClick(sizeNames[sizeLabel])}
            key={key}
          />
        ))}
      </section>

      <SectionHeader>Or input a range ({displaySizeUnits})</SectionHeader>

      {Object.keys(inputFields).map((inputGroup, key) => (
        <React.Fragment key={key}>
          <p className="light-text">{capitalize(inputGroup)}</p>

          <TextFieldGroup>
            {Object.keys(inputFields[inputGroup]).map((inputName, key) => (
              <React.Fragment key={key}>
                <Textfield
                  placeholder={capitalize(inputName)}
                  disabled={false}
                  value={inputFields[inputGroup][inputName].value}
                  error={inputFields[inputGroup][inputName].error}
                  callback={(val) => onInputChange(inputGroup, inputName, val)}
                  icon={inputFields[inputGroup][inputName].error ? 'alert' : undefined}
                />
                {Object.keys(inputFields[inputGroup]).length > key + 1 && <span>-</span>}
              </React.Fragment>
            ))}
          </TextFieldGroup>
        </React.Fragment>
      ))}

      {errorList.length > 0 && (
        <section className="error-text">
          {errorList
            .filter((error, index) => errorList.findIndex((e) => e.msg === error.msg) === index)
            .map((error, key) => (
              <p key={key}>
                <Icon type="alert" /> {error.msg}
              </p>
            ))}
        </section>
      )}
    </StyledSizeBody>
  );

  const collapsibleFragment = () => (
    <Accordion title={title} subtitle={subtitle} defaultCollapsed={defaultCollapsed}>
      {fragmentBody()}
    </Accordion>
  );

  const fragment = () => (
    <StyledSize>
      <h4>{title}</h4>

      <p>{subtitle}</p>

      {fragmentBody()}
    </StyledSize>
  );

  return collapsible === true ? collapsibleFragment() : fragment();
};

export default Size;
